import React, { ReactElement } from 'react';

import { Inject } from '@odinjs/odin';

import { Endpoint, FULL_ROUTES, getFilterDates, isContrato, StatusBeneficiario } from '@philips-emr/hpms-stipulator-commons';
import { HPMSOnCreateMenu, HPMSOnCreateOption, HPMSOnOptionClick } from '@philips-emr/hpms-stipulator-components/lib/events/event';
import { HpmsCustomCardEvent, HpmsCustomCardMenuRenderEvent } from '@philips-emr/hpms-stipulator-components/lib/types';
import { FetchAPI, FieldAPI, FilterAPI, PanelAPI, Stamp, StampAPI, StampIcon, TextAPI } from '@philips-emr/tws-components';
import { Color } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnClear, OnClose, OnDynamicContentRender, OnMount, OnSubmit } from '@philips-emr/tws-core/lib/client';
import { ClearEvent, MountEvent, RenderEvent, SubmitEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService } from '@philips-emr/tws-core/lib/services';
import { applicationContext } from '@philips-emr/tws-foundation';

import BaseController from './BaseController';
import { BeneficiarioLoteData } from './lote/lote-utils';

import './colors.css';

interface Data {
  nrSeqInclusao: number;
  nrSeqLote: number;
  dsStatus: string;
  ieStatus: StatusBeneficiario;
  nmPessoaFisica: string;
  ieInconsistencia: string;
}

const OptionKey = {
  DETALHES: 'detalhes',
  IMPRIMIR: 'imprimir',
  REENVIAR: 'reenviar',
};
const statusImprimir = ['B', 'C', 'P', 'A'];

function getStamp(ieStatus: StatusBeneficiario, label: string): ReactElement | null {
  let stampProps = null;

  switch (ieStatus) {
    case StatusBeneficiario.A:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
      break;
    case StatusBeneficiario.AL:
      stampProps = { color: Color.SAFETY_YELLOW, icon: StampIcon.WARNING, label };
      break;
    case StatusBeneficiario.B:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CLOCK, label };
      break;
    case StatusBeneficiario.C:
      stampProps = { color: Color.SAFETY_BLUE, icon: StampIcon.CHECK, label };
      break;
    case StatusBeneficiario.I:
      stampProps = { color: Color.SAFETY_ORANGE, icon: StampIcon.WARNING, label };
      break;
    case StatusBeneficiario.R:
      stampProps = {
        className: 'c-stamp-custom-color-purple',
        icon: StampIcon.CANCELED_OUTLINE,
        label,
      };
      break;
    case StatusBeneficiario.P:
      stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label };
      break;
    case StatusBeneficiario.RO:
      stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
      break;
    case StatusBeneficiario.PENDENTE:
      stampProps = { color: Color.SAFETY_NEUTRAL, icon: StampIcon.CLOCK, label };
      break;
    case StatusBeneficiario.CONFIRMADA:
      stampProps = { color: Color.SAFETY_GREEN, icon: StampIcon.CHECK, label };
      break;
    case StatusBeneficiario.REJEITADA:
      stampProps = { color: Color.SAFETY_RED, icon: StampIcon.CANCELED, label };
      break;

    default:
      return null;
  }
  return <Stamp {...stampProps} />;
}

@Controller({ fragment: 'consulta-historico-inclusao-beneficiario' })
class ConsultaHistoricoInclusaoBeneficiario extends BaseController {

  @Inject('fetch-consulta')
  private fetchConsulta: FetchAPI;
  @Inject('panel-modal')
  private panelModal: PanelAPI;
  @Inject('text-motivo')
  private textMotivoApi: TextAPI;
  @Inject('stamp-situacao-lote')
  private stampSituacaoApi: StampAPI;

  @OnClear({ target: 'filter-consulta' })
  onClearFilter(event: ClearEvent): void {
    const api = event.target as unknown as FilterAPI;
    api.setData({});
    api.setData(getFilterDates('dtInicio', 'dtFim', 3));

  }

  @OnSubmit({ target: 'filter-consulta' })
  onSubmitFilter(event: SubmitEvent): void {
    const { record, target } = event;
    const api = target as unknown as FilterAPI;

    const domain = isContrato() ? 2697 : 4968;
    this.context.set({ domain });
    const ieTipoOperacao = 'I';
    const nrSeqSubestipulante = this.context.get('nrSeqSubestipulante');

    nrSeqSubestipulante && this.fetchConsulta.setParameters({ ...this.fetchConsulta.getParameters(), nrSeqSubestipulante });
    ieTipoOperacao && this.fetchConsulta.setParameters({ ...this.fetchConsulta.getParameters(), ieTipoOperacao });
    this.fetchConsulta.setParameters({ ...this.fetchConsulta.getParameters(), loteIsOptional: true });

    if (record && record.dtInicio && record.dtFim) {
      return;
    }

    const { dtInicio, dtFim } = getFilterDates('dtInicio', 'dtFim', 3);
    !record.dtInicio && (record.dtInicio = dtInicio);
    !record.dtFim && (record.dtFim = dtFim);
    const ieLote = {
      dsValorDominio: 'Todos',
      vlDominio: 'T',
    };

    api.setData({});
    api.setData({ ...api.getRecord(), dtInicio: record.dtInicio, dtFim: record.dtFim, ieLote });
  }

  @OnDynamicContentRender({ target: 'render-status' })
  onDynamicContentRender(event: RenderEvent): void {
    const { data, render } = event;
    const { ieStatus, dsStatus } = data as Data;

    const stamp = getStamp(ieStatus, dsStatus);

    if (stamp) {
      render((): ReactElement => stamp);
    }

  }

  @HPMSOnCreateMenu('hpms-custom-card')
  onCreateCardMenu(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, shouldRender } = event;
    const { ieStatus } = data as unknown as Data;

    if (ieStatus != StatusBeneficiario.RO) {
      shouldRender(false);
    }
  }

  @HPMSOnCreateOption('hpms-custom-card')
  onCreateOption(event: HpmsCustomCardMenuRenderEvent<Record<string, Value>>): void {
    const { data, key, shouldRender } = event;
    const { ieStatus } = data as unknown as Data;

    const show = {
      [OptionKey.DETALHES]: [StatusBeneficiario.RO].includes(ieStatus),
      [OptionKey.IMPRIMIR]: this.renderizaImprimir(data as unknown as Data),
      [OptionKey.REENVIAR]: [StatusBeneficiario.RO].includes(ieStatus),
    };

    shouldRender(show[key]);
  }

  @HPMSOnOptionClick('hpms-custom-card')
  onOptionClick(event: HpmsCustomCardEvent<Record<string, Value>>): void {
    const { data, key } = event;
    const { nrSeqInclusao } = data as unknown as Data;

    const action = {
      [OptionKey.DETALHES]: () => this.detalhes(nrSeqInclusao),
      [OptionKey.REENVIAR]: () => this.reenviar(nrSeqInclusao),
    };

    if (action[key]) {
      action[key]();
    }
  }

  @OnClose({ target: 'modal-rejeicao' })
  onClose(): void {
    this.panelModal.setVisible(false);
  }

  @OnMount('field-nrSeqLote')
  onMountFieldNrSeqLote(event: MountEvent): void {
    const api = event.target as unknown as FieldAPI;
    const data = api.getData() as unknown as Data;

    if (!data.nrSeqLote) {
      api.setLabel('');
    }
  }

  private reenviar(nrSeqInclusao: number): void {
    applicationContext.redirect(FULL_ROUTES.BENEF_SEQ_REENVIAR_INCLUSAO, { nrSeqInclusao });
  }

  private detalhes(nrSeqInclusao: number): void {

    const { nrSeqContrato } = this.historyService.getCurrentRouteParams();

    void HttpService.get<BeneficiarioLoteData[]>(Endpoint.GET.BENEFICIARIO_INCLUSAO_STATUS, { nrSeqContrato, nrSeqInclusao })
      .then(response => {
        const { body } = response;

        if (!body) {
          return;
        }

        const { dsMotivoReprovacao, dsStatus } = body as unknown as BeneficiarioLoteData;
        this.panelModal.setVisible(true);
        this.textMotivoApi.setValue(dsMotivoReprovacao);
        this.stampSituacaoApi.setLabel(dsStatus);

      });
  }

  private renderizaImprimir(data: Data): boolean {
    const { ieInconsistencia, ieStatus } = data;
    if (ieInconsistencia === 'N' && statusImprimir.includes(ieStatus)) {
      return true;
    }
    return false;
  }

}

export default ConsultaHistoricoInclusaoBeneficiario;
